import React, { useEffect, useState } from "react";
import AnimatedNeurons from "@/components/AnimatedNeurons";

/**
 * PWASplashScreen — schermata di apertura mostrata SOLO quando l'app è aperta
 * in modalità standalone (PWA installata). Riusa i neuroni animati del login
 * per dare la sensazione che l'app "si accenda" con la stessa scena cinematografica.
 *
 * - Rileva `display-mode: standalone` (Android/Chrome/Edge) o `navigator.standalone` (iOS)
 * - Viene mostrata UNA volta per sessione (sessionStorage) — se ricarichi la pagina non riparte
 * - Auto-dismiss dopo ~3.6s (durata sufficiente per veder crescere i neuroni)
 * - Rispetta prefers-reduced-motion (durata ridotta)
 */
export default function PWASplashScreen() {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (!standalone) return;
    // Solo la prima navigazione in questa sessione
    try {
      if (sessionStorage.getItem("syn_splash_seen") === "1") return;
      sessionStorage.setItem("syn_splash_seen", "1");
    } catch { /* ignore private mode */ }

    setVisible(true);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const showFor  = reduced ? 900 : 3400;
    const fadeFor  = 500;

    const t1 = setTimeout(() => setLeaving(true), showFor);
    const t2 = setTimeout(() => setVisible(false), showFor + fadeFor);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-splash"
      className="fixed inset-0 z-[9999] bg-[#020615] flex flex-col items-center justify-center overflow-hidden pointer-events-none"
      style={{
        opacity: leaving ? 0 : 1,
        transition: "opacity 500ms cubic-bezier(0.22,1,0.36,1)",
      }}>
      {/* Neuroni procedurali che si disegnano */}
      <div className="absolute inset-0">
        <AnimatedNeurons startDelay={0.2} />
        {/* vignette per fondere i bordi */}
        <div className="absolute inset-0"
             style={{
               background:
                 "radial-gradient(70% 60% at 50% 50%, rgba(0,0,0,0) 0%, rgba(2,6,21,0.35) 55%, rgba(2,6,21,0.85) 90%, #020615 100%)",
             }} />
      </div>

      {/* Wordmark centrale */}
      <div className="relative z-10 text-center"
           style={{
             animation: "syn-splash-word 900ms cubic-bezier(0.22,1,0.36,1) 1.6s both",
           }}>
        <h1 className="font-heading text-5xl md:text-6xl font-black tracking-tight
                       bg-gradient-to-r from-[#67E8F9] via-white to-[#FDE68A] bg-clip-text text-transparent">
          Synappsi
        </h1>
        <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-white/60 mt-2">
          Connessione intelligente
        </p>
      </div>

      <style>{`
        @keyframes syn-splash-word {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}
